// src/helpers/downloadMeme.js

// or wherever your isIosSafari() logic is located

/**
 * Downloads (or shares) the meme in full resolution,
 * using the original BG size and drawing each layer scaled up.
 *
 * @param {object} options - All needed references/data
 * @param {RefObject<HTMLImageElement>} options.uploadedImageObj
 * @param {RefObject<DOMRect>} options.bgRect
 * @param {RefObject<Array>} options.layers
 */
export async function downloadMemeHighRes({ uploadedImageObj, layers, bgRect }) {
	// 1) Make sure we have a background
	if (!uploadedImageObj?.current) return;

	// 2) Create offscreen canvas at the original background size
	const originalWidth = uploadedImageObj.current.width;
	const originalHeight = uploadedImageObj.current.height;
	const offCanvas = document.createElement("canvas");
	offCanvas.width = originalWidth;
	offCanvas.height = originalHeight;
	const offCtx = offCanvas.getContext("2d");

	// 3) Calculate scale
	const { x: bgX, y: bgY, width: bgW, height: bgH } = bgRect.current;
	const scaleX = originalWidth / bgW;
	const scaleY = originalHeight / bgH;

	// 4) Draw the background
	offCtx.drawImage(uploadedImageObj.current, 0, 0, originalWidth, originalHeight);

	// 5) Draw each layer in high-res
	layers.current.forEach((layer) => {
		if (layer.type === "asset") {
			drawAssetLayerHighRes(offCtx, layer, bgX, bgY, scaleX, scaleY);
		} else if (layer.type === "text") {
			drawTextLayerHighRes(offCtx, layer, bgX, bgY, bgW, bgH, scaleX, scaleY);
		}
	});

	// 6) Convert to dataURL -> Blob -> File
	const dataURL = offCanvas.toDataURL("image/png", 1.0);
	const response = await fetch(dataURL);
	const blob = await response.blob();
	const file = new File([blob], "meme.png", { type: "image/png" });

	// 7) iOS Safari => share; else => normal download
	if (isIosSafari()) {
		if (navigator.canShare && navigator.canShare({ files: [file] })) {
			try {
				await navigator.share({
					files: [file],
					title: "My Meme",
					text: "Check out this meme!",
				});
				return; // success or user canceled
			} catch (err) {
				console.warn("Share canceled or failed:", err);
				// fallback or do nothing
			}
		}
		// If we reach here, do nothing or add fallback as needed
	} else {
		// Desktop or non-iOS fallback: classic download
		const link = document.createElement("a");
		link.href = dataURL;
		link.download = "meme.png";
		link.click();
	}
}

export function drawAssetLayerHighRes(ctx, layer, bgX, bgY, scaleX, scaleY) {
	// The on-screen asset is at (layer.x, layer.y), sized (layer.width, layer.height)
	// We need to scale that position & size to offCanvas.
	const offsetX = layer.x - bgX; // distance from BG left
	const offsetY = layer.y - bgY; // distance from BG top

	const scaledX = offsetX * scaleX;
	const scaledY = offsetY * scaleY;
	const scaledW = layer.width * scaleX;
	const scaledH = layer.height * scaleY;

	ctx.save();
	// Move to the center of the scaled asset
	const centerX = scaledX + scaledW / 2;
	const centerY = scaledY + scaledH / 2;
	ctx.translate(centerX, centerY);
	ctx.rotate((layer.rotation * Math.PI) / 180);

	// draw the image with center in mind
	ctx.drawImage(layer.image, -scaledW / 2, -scaledH / 2, scaledW, scaledH);
	ctx.restore();
}

// ---------------------------------------------------------------------------
// drawTextLayerHighRes
// ---------------------------------------------------------------------------
export function drawTextLayerHighRes(ctx, layer, bgX, bgY, bgW, bgH, scaleX, scaleY) {
	// EXACTLY replicate your multiline logic from the main canvas,
	// but at "scaled" sizes. We'll do:
	//
	// 1) offset to bgRect
	// 2) scale the (x,y)
	// 3) scale the font
	// 4) re-wrap the text using measureText at the scaled font size
	// 5) center each line around (0,0), as you do in the main

	const {
		x,
		y,
		rotation,
		fontSize,
		color,
		text,
		fontFamily,
		isBold,
		isItalic,
		isUnderlined,
		isStrikethrough,
		isAllCaps,
		opacity,
	} = layer;

	// (1) offset from BG
	const offsetX = x - bgX;
	const offsetY = y - bgY;

	// (2) scale positions
	const scaledX = offsetX * scaleX;
	const scaledY = offsetY * scaleY;

	// (3) scale the font size (use whichever dimension you prefer,
	//     often the min of scaleX/scaleY is OK if BG was uniformly scaled).
	const finalFontSize = fontSize * Math.min(scaleX, scaleY);

	// Build the font descriptor
	let fontDesc = "";
	if (isItalic) fontDesc += "italic ";
	if (isBold) fontDesc += "bold ";
	fontDesc += `${finalFontSize}px ${fontFamily || "Impact"}`;

	// (4) measure text lines
	ctx.save();
	ctx.font = fontDesc;
	const displayText = isAllCaps ? text.toUpperCase() : text;

	// If you want the same approximate maxWidth that the user had,
	// you could replicate: let maxWidth = something scaled.
	// If you used (canvas.width - 10) on screen, then here you might do:
	//   let maxWidth = (bgW - 10) * scaleX;
	// but some folks prefer a simpler approach. Let’s do the same ratio:
	const maxWidth = (bgW - 10) * scaleX;

	const lines = wrapText(ctx, displayText, maxWidth);
	const lineHeight = finalFontSize * 1.2;
	const boundingBoxH = lines.length * lineHeight;

	// measure each line's width to find boundingBoxW
	let boundingBoxW = 0;
	let lineWidths = [];
	for (const ln of lines) {
		const w = ctx.measureText(ln).width;
		lineWidths.push(w);
		if (w > boundingBoxW) boundingBoxW = w;
	}

	// Now we can draw
	ctx.translate(scaledX, scaledY);
	ctx.rotate((rotation * Math.PI) / 180);
	ctx.globalAlpha = opacity ?? 1.0;

	// Set text styles (shadow, outline, etc.)
	ctx.shadowColor = "rgba(0, 0, 0, 0.7)";
	ctx.shadowBlur = 4 * Math.min(scaleX, scaleY);
	ctx.shadowOffsetX = 2 * scaleX;
	ctx.shadowOffsetY = 2 * scaleY;

	// Outline color
	const outlineColor = color === "#ffffff" ? "#000000" : "#ffffff";
	ctx.lineWidth = Math.ceil(finalFontSize * 0.07);
	ctx.strokeStyle = outlineColor;
	ctx.lineJoin = "round";
	ctx.miterLimit = 2;
	ctx.textAlign = "center";
	ctx.textBaseline = "middle";

	// The entire multiline block is centered at (0,0).
	// topY = - (boundingBoxH / 2). Then line i => y offset
	const topY = -boundingBoxH / 2;

	// Loop over lines
	for (let i = 0; i < lines.length; i++) {
		const line = lines[i];
		const lineY = topY + i * lineHeight + lineHeight / 2;

		// stroke + fill
		ctx.strokeText(line, 0, lineY);
		ctx.fillStyle = color;
		ctx.fillText(line, 0, lineY);

		// underline or strikethrough
		if (isUnderlined || isStrikethrough) {
			const thisLineWidth = ctx.measureText(line).width;
			const halfW = thisLineWidth / 2;

			// underlined => near the baseline
			if (isUnderlined) {
				const baselineShift = finalFontSize * 0.35;
				const underlineY = lineY + baselineShift + 5 * Math.min(scaleX, scaleY);
				ctx.beginPath();
				ctx.moveTo(-halfW, underlineY);
				ctx.lineTo(halfW, underlineY);
				ctx.lineWidth = Math.max(2, finalFontSize * 0.03);
				ctx.strokeStyle = color;
				ctx.stroke();
			}

			// strikethrough => center line
			if (isStrikethrough) {
				const strikeY = lineY;
				ctx.beginPath();
				ctx.moveTo(-halfW, strikeY);
				ctx.lineTo(halfW, strikeY);
				ctx.lineWidth = Math.max(2, finalFontSize * 0.06);
				ctx.strokeStyle = color;
				ctx.stroke();
			}
		}
	}

	ctx.restore();
}

export function isIosSafari() {
	const ua = window.navigator.userAgent;
	return /iPad|iPhone|iPod/.test(ua) && !window.MSStream && /Safari/.test(ua) && !/CriOS/.test(ua);
}

function wrapText(ctx, text, maxWidth) {
	const lines = [];
	const paragraphs = text.split("\n");
	paragraphs.forEach((paragraph) => {
		const words = paragraph.split(" ");
		let currentLine = words[0] || "";
		for (let i = 1; i < words.length; i++) {
			const testLine = currentLine + " " + words[i];
			const metrics = ctx.measureText(testLine.trim());
			if (metrics.width > maxWidth) {
				lines.push(currentLine);
				currentLine = words[i];
			} else {
				currentLine = testLine;
			}
		}
		lines.push(currentLine);
	});
	return lines.map((l) => l.trim());
}
